import { normalizeChecklistSnapshotHistoricalRow } from '../checklist/ChecklistSnapshotRemoteCreateService';
import { matchesHistoricalArchive } from './historicalArchiveCompatibility';
import { stableStringify } from '../checklist/ChecklistSnapshotCreationService';

export type HistoricalRow = Record<string, any>;
export const historicalTables = {
  parents: 'guincho_checklists', schemas: 'guincho_template_schema_versions',
  snapshots: 'guincho_checklist_snapshots', fields: 'guincho_checklist_snapshot_fields',
};
export interface HistoricalHydrationDependencies {
  enabled(): Promise<boolean>;
  listParents(companyId: string): Promise<HistoricalRow[]>;
  read(table: string, id: string): Promise<HistoricalRow | null>;
  fetch(table: string, filters: Record<string, string | null>, offset: number, limit: number): Promise<HistoricalRow[]>;
  refreshProtection?(): Promise<void>;
  allowed(table: string, row: HistoricalRow): Promise<boolean>;
  insert(table: string, row: HistoricalRow): Promise<unknown>;
  link(id: string, snapshotId: string, expected: HistoricalRow): Promise<boolean>;
}
const object = (value: any) => typeof value === 'string' ? JSON.parse(value) : value;
export const sameHistoricalIdentity = (parent: HistoricalRow, snapshot: HistoricalRow): boolean => Boolean(
  parent.snapshot_id && snapshot.id === parent.snapshot_id && snapshot.checklist_id === parent.id
  && snapshot.empresa_id === parent.empresa_id && snapshot.template_id === parent.template_id
);

export class HistoricalSnapshotHydrationService {
  constructor(private readonly deps: HistoricalHydrationDependencies) {}

  async run(companyId: string): Promise<number> {
    if (!companyId || !await this.deps.enabled()) return 0;
    const parents = await this.deps.listParents(companyId);
    let linked = 0;
    for (const parent of parents) {
      if (parent.empresa_id !== companyId || parent.snapshot_id != null) continue;
      const remoteRows = await this.deps.fetch(historicalTables.parents, { id: parent.id, empresa_id: companyId }, 0, 2);
      const remote = remoteRows[0];
      if (remoteRows.length !== 1 || !remote?.snapshot_id || remote.retired_at != null || remote.id !== parent.id
        || remote.empresa_id !== companyId || remote.template_id !== parent.template_id
        || remote.usuario_id !== parent.usuario_id) continue;
      const rows = await loadHistoricalSnapshotRows(this.deps, remote);
      if (!rows) continue;
      const snapshot = rows.find(([table]) => table === historicalTables.snapshots)![1];
      await this.deps.refreshProtection?.();
      let valid = await this.deps.allowed(historicalTables.parents, parent);
      for (const [table, row] of rows) {
        const existing = await this.deps.read(table, row.id);
        // Arquivos já presentes são imutáveis; divergências nunca são corrigidas por conteúdo.
        if (!await this.deps.allowed(table, row) || (existing && !matchesHistoricalArchive(table, existing, row))) valid = false;
      }
      if (!valid) continue;
      await this.deps.refreshProtection?.();
      for (const [table, row] of rows) {
        if (!await this.deps.allowed(historicalTables.parents, parent) || !await this.deps.allowed(table, row)) { valid = false; break; }
        const existing = await this.deps.read(table, row.id);
        if (existing && !matchesHistoricalArchive(table, existing, row)) { valid = false; break; }
        if (!existing) await this.deps.insert(table, row);
        const stored = await this.deps.read(table, row.id);
        if (!stored || !matchesHistoricalArchive(table, stored, row)) { valid = false; break; }
      }
      if (!valid) continue;
      await this.deps.refreshProtection?.();
      for (const [table, row] of rows) {
        const stored = await this.deps.read(table, row.id);
        if (!stored || !matchesHistoricalArchive(table, stored, row) || !await this.deps.allowed(table, row)) { valid = false; break; }
      }
      if (!valid) continue;
      const current = await this.deps.read(historicalTables.parents, parent.id);
      if (!current || current.snapshot_id != null || current.empresa_id !== companyId
        || current.template_id !== parent.template_id || current.usuario_id !== parent.usuario_id
        || !await this.deps.allowed(historicalTables.parents, current)) continue;
      if (await this.deps.link(parent.id, snapshot.id, current)) linked++;
    }
    return linked;
  }
}

/** Valida o arquivo remoto antes de qualquer materialização, sem alterar snapshots. */
export const loadHistoricalSnapshotRows = async (
  deps: Pick<HistoricalHydrationDependencies, 'fetch'>,
  remote: HistoricalRow,
): Promise<Array<[string, HistoricalRow]> | null> => {
  const companyId = remote.empresa_id;
  const snapshots = await deps.fetch(historicalTables.snapshots, { id: remote.snapshot_id, empresa_id: companyId }, 0, 2);
  const snapshot = snapshots[0];
  if (snapshots.length !== 1 || !snapshot || !sameHistoricalIdentity(remote, snapshot)) return null;
  const schemas = await deps.fetch(historicalTables.schemas, { id: snapshot.template_schema_version_id, empresa_id: companyId }, 0, 2);
  const schema = schemas[0];
  if (schemas.length !== 1 || !schema || schema.id !== snapshot.template_schema_version_id
    || schema.empresa_id !== companyId || schema.template_id !== remote.template_id
    || !schema.schema_hash || schema.schema_hash !== snapshot.schema_hash
    || stableStringify(object(schema.schema_payload)) !== stableStringify(object(snapshot.schema_payload))) return null;
  const fields: HistoricalRow[] = [];
  for (let offset = 0; ; offset += 200) {
    const page = await deps.fetch(historicalTables.fields, { snapshot_id: snapshot.id, empresa_id: companyId }, offset, 200);
    fields.push(...page);
    if (page.length < 200) break;
  }
  const snapshotPayload = object(snapshot.schema_payload);
  const expectedFields = snapshotPayload?.fields;
  if (snapshotPayload?.template_id !== remote.template_id || !Array.isArray(expectedFields) || fields.length !== expectedFields.length
    || new Set(fields.map(field => field.id)).size !== fields.length
    || new Set(fields.map(field => field.field_key)).size !== fields.length
    || fields.some(field => !field.id || !field.field_key || field.snapshot_id !== snapshot.id || field.empresa_id !== companyId
      || !expectedFields.some((expected: HistoricalRow) => expected.field_key === field.field_key
        && (expected.original_campo_id ?? null) === (field.original_campo_id ?? null)
        && (expected.id == null || expected.id === field.id)))) return null;
  const rawRows: Array<[string, HistoricalRow]> = [
    [historicalTables.schemas, schema], [historicalTables.snapshots, snapshot],
    ...fields.map(field => [historicalTables.fields, field] as [string, HistoricalRow]),
  ];
  const rows: Array<[string, HistoricalRow]> = rawRows.map(([table, row]) => [table, normalizeChecklistSnapshotHistoricalRow(table, row)]);
  return rows;
};
